import { Input } from 'antd'
import React, { forwardRef } from 'react'
/**
 * 自定义文本域组件
 *
 * 这是一个基于Ant Design TextArea封装的文本域组件，支持多行文本输入。
 * 使用forwardRef支持ref转发，便于父组件直接操作文本域。
 *
 * @param className - 自定义CSS类名
 * @param autoSize - 自动调整高度，可以是布尔值或配置对象
 * @param props - 其他传递给TextArea组件的属性
 * @param ref - 转发给TextArea组件的ref引用
 *
 * 使用场景：
 * 1. 表单输入 - 多行文本的表单输入
 * 2. 评论输入 - 用户评论和反馈的输入
 * 3. 内容编辑 - 文章、描述等内容的编辑
 * 4. 备注输入 - 各种备注和说明的输入
 * 5. 消息输入 - 聊天消息的输入框
 * 6. 配置说明 - 配置项的详细说明输入
 * 7. 问题描述 - 问题报告和描述输入
 * 8. 日志记录 - 操作日志的记录输入
 *
 * 使用示例：
 * ```tsx
 * // 基础用法
 * const [value, setValue] = useState('')
 *
 * <CustomTextArea
 *   value={value}
 *   onChange={e => setValue(e.target.value)}
 *   placeholder="请输入内容"
 *   rows={4}
 * />
 *
 * // 自动调整高度
 * <CustomTextArea
 *   autoSize={{ minRows: 2, maxRows: 6 }}
 *   placeholder="自动调整高度的文本域"
 * />
 *
 * // 使用ref
 * const textAreaRef = useRef<HTMLTextAreaElement>(null)
 *
 * <CustomTextArea
 *   ref={textAreaRef}
 *   placeholder="可聚焦的文本域"
 * />
 *
 * // 禁用状态
 * <CustomTextArea
 *   disabled
 *   value="只读内容"
 * />
 * ```
 */

const { TextArea } = Input

// 定义文本区域的属性类型
interface CustomTextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  autoSize?: boolean | { minRows?: number; maxRows?: number } // 自动调整高度配置
  // 继承HTML textarea元素的所有属性
}

const CustomTextArea = forwardRef<HTMLTextAreaElement, CustomTextAreaProps>(({ className, ...props }: any, ref) => (
  <TextArea className={className} ref={ref} {...props} />
))

export default CustomTextArea
